import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalWrapper from '../../../components/modalWrapper';
import CustomSelect from '../../../components/inputs/select/CustomSelect';
import Loading from '../../../components/common/Loading';
import { Request } from '../../../config/requests';

const MIN_AMOUNT = 0.5;
const MAX_AMOUNT = 500;

/**
 * Charge one or several instalments.
 *
 * The amount is editable per line because an admin charging by hand is usually charging
 * something other than the instalment's face value — a part payment, a rounded figure, a
 * corrected one. The bounds are the backend's: Stripe will not take less than 0.50, and
 * 500 is the ceiling that catches a slipped decimal point before the customer does.
 *
 * WHAT MAY BE CHARGED IS NOT DECIDED HERE. This dialog collects amounts and intent; the
 * server resolves every line against Stripe immediately before charging it and refuses
 * what must not go through — already paid, still clearing, unreadable. Its answer comes
 * back per invoice and is shown below, so a partly-blocked batch reads as exactly that.
 */
function ModalBulkCharge({
    openModal,
    setOpenModal,
    invoices = [],
    allInvoices = [],
    policy = {},
    token,
    onComplete,
}) {
    const { t } = useTranslation();
    const [step, setStep] = useState('edit'); // 'edit' | 'loading' | 'result'
    // The WORKING SET, not the props. A blocked line (already paid, still clearing) is the
    // normal way this ends, and the admin's next move is to drop that invoice and charge
    // the rest — which means the list has to be editable here rather than sending them
    // back to the grid to re-tick everything.
    const [lines, setLines] = useState([]);
    const [amount, setAmount] = useState('');
    const [addValue, setAddValue] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const suggested = useMemo(() => lines
        .reduce((sum, invoice) => sum + (Number(invoice.payment_amount) || 0), 0), [lines]);

    useEffect(() => {
        if (!openModal) return;
        setLines(invoices);
        setStep('edit');
        setResult(null);
        setError(null);
        setAddValue(null);
    }, [openModal, invoices]);

    // Only while the admin has not touched the amount: once they type a figure it is
    // theirs, and removing a line must not silently rewrite it.
    const [amountTouched, setAmountTouched] = useState(false);
    useEffect(() => {
        if (!amountTouched) setAmount(suggested.toFixed(2));
    }, [suggested, amountTouched]);

    const statusFor = (invoiceNumber) => (result?.lines || [])
        .find((line) => String(line.invoiceNo) === String(invoiceNumber));

    // A line the server already refused — paid, clearing, unreadable. It stays visible,
    // in red, and BLOCKS Confirm: the charge is all-or-nothing because one amount covers
    // the whole set, so the admin has to take it out (or put a different invoice in)
    // before anything can go through.
    const blockedLines = lines.filter((line) => {
        const verdict = statusFor(line.invoice_number);
        return verdict && verdict.chargeable === false;
    });

    const value = Number(amount);
    const invalid = blockedLines.length > 0
        ? `${t('removeBlockedInvoices')}: ${blockedLines.map((line) => line.invoice_number).join(', ')}`
        : !Number.isFinite(value)
        ? t('amountIsRequired')
        : value < MIN_AMOUNT
            ? `${t('Amount')} < ${MIN_AMOUNT.toFixed(2)}`
            : value > MAX_AMOUNT
                ? `${t('Amount')} > ${MAX_AMOUNT}`
                : lines.length === 0
                    ? t('noInvoicesSelected')
                    : null;

    function handleClose() {
        const charged = step === 'result' && (result?.charged || 0) > 0;
        setOpenModal(false);
        setStep('edit');
        setResult(null);
        setError(null);
        setAmountTouched(false);
        if (charged && onComplete) onComplete();
    }

    function removeLine(invoiceNumber) {
        setLines((prev) => prev.filter((line) => String(line.invoice_number) !== String(invoiceNumber)));
        // The verdicts belong to the list that produced them.
        setResult(null);
        setStep('edit');
    }

    /**
     * What else could go in this charge: the policy's instalments that are NOT already
     * listed and NOT already paid.
     *
     * Typing a number was the wrong control — an admin does not know the numbers by
     * heart, and a typo could only ever be rejected. A list of what is actually available
     * cannot be wrong.
     */
    const addOptions = useMemo(() => allInvoices
        .filter((invoice) => !lines.some((line) => String(line.invoice_number) === String(invoice.invoice_number)))
        .filter((invoice) => !(invoice.paid === true && invoice.payment_status === 'PAID'))
        .sort((a, b) => Number(a.invoice_number) - Number(b.invoice_number))
        .map((invoice) => ({
            value: String(invoice.invoice_number),
            label: `${invoice.invoice_number} · ${invoice.due_date} · ${Number(invoice.payment_amount || 0).toFixed(2)}`,
        })), [allInvoices, lines]);

    function addLine(option) {
        if (!option?.value) return;
        const found = allInvoices.find((invoice) => String(invoice.invoice_number) === String(option.value));
        if (!found) return;

        setLines((prev) => [...prev, found].sort((a, b) => Number(a.invoice_number) - Number(b.invoice_number)));
        setAddValue(null);
        // The verdicts belong to the list that produced them.
        setResult(null);
        setStep('edit');
    }

    async function handleConfirm() {
        if (invalid) return;
        setStep('loading');
        setError(null);

        try {
            const response = await Request({
                method: 'post',
                route: 'api/payment/charge_bulk',
                token,
                values: {
                    policyId: Number(policy.policyId),
                    policyNumber: policy.policyNumber,
                    stripeSubscription: policy.stripeSubscription,
                    email: policy.email,
                    autoAdvance: true,
                    invoiceNos: lines
                        .slice()
                        .sort((a, b) => Number(a.invoice_number) - Number(b.invoice_number))
                        .map((line) => String(line.invoice_number)),
                    amount: value,
                },
            });

            const body = await response.json().catch(() => null);
            if (!response.ok) {
                setError(body?.message || body?.errors?.join(', ') || t('chargeFailed'));
                setStep('result');
                return;
            }
            setResult(body);
            setStep('result');
        } catch (err) {
            setError(err.message || t('chargeFailed'));
            setStep('result');
        }
    }

    const charged = result?.status === 'ok' || result?.status === 'partial';

    // Fixed column widths. The status sentence is long and variable — left to size
    // itself it dragged the numbers out of alignment on every row, which is the one thing
    // a column of amounts must not do.
    const CELL = { padding: '6px 8px 6px 0', verticalAlign: 'top' };

    const renderLineTable = ({ editable }) => (
        <table style={{ width: '100%', margin: '12px 0', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                <col style={{ width: '104px' }} />
                <col style={{ width: '112px' }} />
                <col style={{ width: '82px' }} />
                <col />
                <col style={{ width: '28px' }} />
            </colgroup>
            <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    <th style={CELL}>{t('Invoice')}</th>
                    <th style={CELL}>{t('dueDate')}</th>
                    <th style={{ ...CELL, textAlign: 'right' }}>{t('Amount')}</th>
                    <th style={CELL} />
                    <th style={{ padding: '6px 0' }} />
                </tr>
            </thead>
            <tbody>
                {lines.map((line) => {
                    const verdict = statusFor(line.invoice_number);
                    return (
                        <tr key={line.invoice_number} style={{ borderBottom: '1px solid #f2f2f2' }}>
                            <td style={CELL}>{line.invoice_number}</td>
                            <td style={{ ...CELL, whiteSpace: 'nowrap' }}>{line.due_date}</td>
                            <td style={{ ...CELL, textAlign: 'right' }}>
                                {Number(line.payment_amount || 0).toFixed(2)}
                            </td>
                            <td style={{ ...CELL, fontSize: '12px', lineHeight: '1.3', wordBreak: 'break-word' }}>
                                <span style={{ color: verdict ? (verdict.chargeable ? '#2e7d32' : '#EB5955') : 'inherit' }}>
                                    {verdict?.message}
                                </span>
                            </td>
                            <td style={{ padding: '6px 0', textAlign: 'right', verticalAlign: 'top' }}>
                                {editable && (
                                    <span
                                        onClick={() => removeLine(line.invoice_number)}
                                        title={t('remove')}
                                        style={{ color: '#EB5955', cursor: 'pointer', fontWeight: 700 }}
                                    >
                                        ✕
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
                <tr style={{ borderTop: '1px solid #ddd', fontWeight: 600 }}>
                    <td style={CELL} colSpan={2}>{t('Sum')}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{suggested.toFixed(2)}</td>
                    <td style={CELL} />
                    <td />
                </tr>
            </tbody>
        </table>
    );

    const renderAdd = () => (
        addOptions.length > 0 && (
            <div style={{ margin: '4px 0 16px' }}>
                <CustomSelect
                    options={addOptions}
                    placeholder={t('addInvoiceNumber')}
                    value={addValue}
                    onChange={addLine}
                    variant="claims-filter"
                    isClearable={false}
                    toTranslate={false}
                />
            </div>
        )
    );

    // The dialog body has no horizontal padding of its own — the existing modals get
    // theirs from `modal_container__body__text`, which a raw <table> never sees. Without
    // this the rows run into the rounded edges of the popup.
    const BODY = { padding: '0 24px', textAlign: 'start' };

    const renderEdit = () => (
        <div style={BODY}>
            <div className="modal_container__body__text" style={{ textAlign: 'start', padding: 0 }}>
                {t('chargeSelectedInvoices')} ({lines.length})
            </div>

            {/* No banner here. Whatever the server refused is printed against the row it
                belongs to; repeating it above the table said the same sentence twice.
                `error` is different — a request that never got a verdict at all. */}
            {error && (
                <div style={{ color: '#EB5955', textAlign: 'start', marginBottom: '8px' }}>
                    {error}
                </div>
            )}

            {renderLineTable({ editable: true })}
            {renderAdd()}

            {/* ONE amount for one debit. Pre-filled with the sum above and editable,
                because an admin charging by hand is often collecting something else — a
                part payment, or a figure agreed with the customer. */}
            <div style={{ marginTop: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontWeight: 600 }}>{t('chargeAmount')}</span>
                    <input
                        type="number"
                        step="0.01"
                        min={MIN_AMOUNT}
                        max={MAX_AMOUNT}
                        value={amount}
                        onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }}
                        style={{
                            width: '130px',
                            padding: '6px 8px',
                            border: `1px solid ${invalid ? '#EB5955' : '#ccc'}`,
                            borderRadius: '4px',
                        }}
                    />
                    <span>EUR</span>
                </div>
                {invalid && (
                    <div style={{ color: '#EB5955', fontSize: '12px', marginTop: '6px' }}>{invalid}</div>
                )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
                <div className="button_red_small" style={{ width: 'unset' }} onClick={handleClose}>
                    {t('cancel')}
                </div>
                <div
                    className="button_red_small"
                    style={{ width: 'unset', opacity: invalid ? 0.5 : 1, pointerEvents: invalid ? 'none' : 'auto' }}
                    onClick={handleConfirm}
                >
                    {t('confirm')}
                </div>
            </div>
        </div>
    );

    const renderResult = () => (
        <div style={BODY}>
            <div
                className="modal_container__body__text"
                style={{ textAlign: 'start', padding: 0, color: charged ? undefined : '#EB5955' }}
            >
                {error || result?.message}
            </div>

            {renderLineTable({ editable: false })}

            {result?.invoiceLink && (
                <div style={{ marginTop: '8px' }}>
                    <a href={result.invoiceLink} target="_blank" rel="noreferrer">{t('invoiceLink')}</a>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <div className="button_red_small" style={{ width: 'unset' }} onClick={handleClose}>
                    OK
                </div>
            </div>
        </div>
    );

    return (
        <ModalWrapper
            headerTitle={t('charge')}
            openModal={openModal}
            setModalWrapper={handleClose}
        >
            {step === 'loading' ? (
                // `noText` + `scroll={false}`: the bare Loading is the full-page overlay
                // with "Your documents are being uploaded", which inside a dialog about
                // taking money reads as a different operation entirely.
                <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
                    <Loading noText fast scroll={false} />
                </div>
            ) : (charged ? renderResult() : renderEdit())}
        </ModalWrapper>
    );
}

export default ModalBulkCharge;
